import { Admin, IAdmin, IUser, User } from '@order-up/models';
import { AdminCreateUserSchema } from '@order-up/schemas';
import { authService } from './auth';
import { createApiClient } from './apiClient';
import { createLogger } from '../utils/logger';

const logger = createLogger('AdminAPI');

const apiClient = createApiClient('/api/v1');

/**
 * Fetches a list of users from the backend API (Firebase Auth + Firestore merged).
 * The API pre-merges Auth + Firestore data so the client doesn't have to fetch each
 * user document individually (which previously caused an N+1 load).
 * Requires admin privileges.
 */
const listAuthUsers = async (limit: number = 50, pageToken?: string): Promise<{ users: User[], nextPageToken?: string }> => {
    const response = await apiClient.get('/admin-users', {
        params: { limit, pageToken },
    });
    const data = response.data || response;
    logger.debug('listAuthUsers response received', { userCount: data?.users?.length || 0 });

    // Convert the merged payload to User models. We tolerate users that may be
    // missing some fields (e.g. email) by falling back to sane defaults.
    // Preserve API-response-only fields (categories, adminRecord) alongside the User model.
    const users: User[] = (data?.users || []).map((u: any) => {
        const user = User.fromJSON({
            ...u,
            email: u.email || '',
        });
        // Attach enrichment fields from the API response (not on the User model)
        (user as any).categories = u.categories;
        (user as any).adminRecord = u.adminRecord;
        (user as any).disabled = u.disabled;

        // Re-attach API-response fields that User.fromJSON() strips (it creates
        // typed Assignment objects from a fixed set of known keys).
        const rawAssignments = u.assignments || [];
        if (user.assignments && rawAssignments.length === user.assignments.length) {
            for (let i = 0; i < user.assignments.length; i++) {
                const raw = rawAssignments[i];
                const target = user.assignments[i] as any;
                // Display names (API-only, not on Assignment model)
                target.tenantName = raw.tenantName;
                target.restaurantName = raw.restaurantName;
                // Status (lean Assignment may not pick it up from bundled fromJSON)
                target.status = raw.status;
            }
        }

        return user;
    });

    return {
        users,
        nextPageToken: data?.nextPageToken,
    };
};

/**
 * Deletes a user via the backend API.
 * Requires admin privileges and a fresh re-auth (`requireRecentAuth(5)`)
 * — the API returns `STALE_AUTH_REAUTH_REQUIRED` otherwise.
 *
 * Active assignments are auto-revoked server-side as part of delete
 * (no client-side flag). Sole-Owner subjects require `successorUid`;
 * otherwise the API returns 409 `LAST_OWNER_BLOCKED` with the
 * affected tenants list so the caller can prompt + retry.
 */
const deleteUser = async (
    uid: string,
    options?: {
        reason?: string;
        successorUid?: string;
        /** Per-tenant successor map (W17). When present, the server
         * resolves the successor for each sole-owner tenant from this
         * map first, falling back to `successorUid` for any tenant
         * not covered. */
        successorsByTenant?: Record<string, string>;
    },
): Promise<{ message: string }> => {
    // `{ success, data: { message } }` — the message is one level down.
    const body = await apiClient.delete<{ message: string }>(`/admin-users/${uid}`, {
        data: {
            confirmHardDelete: true,
            reason: options?.reason,
            successorUid: options?.successorUid,
            successorsByTenant: options?.successorsByTenant,
        },
    });
    return { message: body.data?.message ?? '' };
};

/**
 * Preview the effects of deleting a user — returns the tenants for
 * which the user is the sole active Owner. Used by the admin Delete
 * dialog (W17) to render the per-tenant successor picker proactively.
 * Read-only; cheap to call; cached briefly via TanStack Query.
 */
const getDeletePreview = async (
    uid: string,
): Promise<{ soleOwnerTenants: Array<{ tenantDocId: string; name?: string }> }> => {
    // The route answers `{ success, data: { soleOwnerTenants } }`. Returning the
    // BODY (as this did) made `preview.soleOwnerTenants` undefined, so the Delete
    // dialog never showed its per-tenant successor picker up front.
    const body = await apiClient.get<{
        soleOwnerTenants: Array<{ tenantDocId: string; name?: string }>;
    }>(`/admin-users/${uid}/delete-preview`);
    return { soleOwnerTenants: body.data?.soleOwnerTenants ?? [] };
};

/**
 * Remove a user (revoke all assignments) via the backend API.
 * Does NOT delete the Firebase account — user enters limbo.
 * Requires admin privileges and a fresh re-auth.
 *
 * `successorUid`, when supplied, is promoted to Owner of any tenant
 * where the subject is the sole active Owner. The API returns
 * `LAST_OWNER_BLOCKED` with `details.affectedTenants` if a successor
 * is required and was not provided — callers should surface the
 * picker and retry.
 */
const removeUser = async (
    uid: string,
    options?: {
        reason?: string;
        successorUid?: string;
        /** W17 — per-tenant successor map. */
        successorsByTenant?: Record<string, string>;
    },
): Promise<{
    assignmentsRevoked: number;
    employeesUnlinked: number;
    invitationsRevoked: number;
}> => {
    const response = await apiClient.post(`/admin-users/${uid}/remove`, {
        reason: options?.reason,
        successorUid: options?.successorUid,
        successorsByTenant: options?.successorsByTenant,
    });
    return response.data || response;
};

/**
 * Updates a user via the backend API.
 * Requires admin privileges.
 */
const updateUser = async (uid: string, userData: Partial<IUser>): Promise<User> => {
    const response = await apiClient.patch(`/admin-users/${uid}`, userData);
    // Use User.fromJSON to handle date conversion automatically
    const userModel = User.fromJSON(response.data.user);
    return userModel;
};

/**
 * Creates a new user via the backend API.
 * Requires admin privileges.
 */
const createUser = async (userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    displayName?: string;
    phoneNumber?: { countryCode: string; number: string } | null;
    photoURL?: string | null;
    emailVerified?: boolean;
    isActive?: boolean;
}): Promise<User> => {
    // Validate input data before sending. The server mints the uid, so this
    // is the admin-create shape (password, no uid) — see AdminCreateUserSchema.
    const validatedInput = AdminCreateUserSchema.parse(userData);
    const response = await apiClient.post('/admin-users', validatedInput);
    // Use User.fromJSON to handle date conversion automatically
    const userModel = User.fromJSON(response.data.user);
    return userModel;
};

/**
 * Signs in with Firebase ID token and establishes admin session.
 * Verifies the token on the server and checks admin privileges.
 * Also generates a custom token for client-side Firebase auth sync.
 */
const signInWithIdToken = async (idToken: string, rememberMe: boolean = false): Promise<{
    user: IUser;
    sessionToken: string;
    expiresAt: string;
    customToken?: string;
}> => {
    try {
        // First, sign in with the admin API to establish server session
        const response = await apiClient.post('/auth/signin', { idToken, rememberMe }) as any;

        logger.debug('SignIn response received');

        // The response interceptor gives us { success: true, data: { user: ... }, meta: ... }
        if (!response.success || !response.data) {
            throw new Error('Invalid response format from auth API');
        }

        logger.debug('User authenticated', { email: response.data.user?.email, isAdmin: response.data.isAdmin });

        // Use User.fromJSON to handle date conversion automatically
        const userModel = User.fromJSON(response.data.user);
        const result = {
            user: userModel,
            sessionToken: response.data.sessionToken,
            expiresAt: response.data.expiresAt,
            customToken: response.data.customToken // Now included in signin response
        };
        logger.info('Sign-in successful', { uid: userModel.uid });
        return result;
    } catch (error) {
        logger.error('Sign-in failed', { error: error instanceof Error ? error.message : String(error) });
        throw error;
    } finally {
        logger.debug('signInWithIdToken completed');
    }
};

/**
 * Upper bound on the best-effort server sign-out call. The httpOnly session
 * cookie can only be cleared by the server, so we still attempt it, but a slow
 * or cold API instance must NEVER stall sign-out — the local Firebase session
 * is already gone by the time we make this call, and the caller redirects
 * regardless of the outcome.
 */
const SIGNOUT_SERVER_TIMEOUT_MS = 4000;

/**
 * Clear only OrderUp's app-specific localStorage keys + all sessionStorage.
 * Local and instant — never makes a network call.
 */
const clearLocalAppStorage = (): void => {
    if (typeof window === 'undefined') return;
    try {
        if (window.localStorage) {
            const keysToRemove: string[] = [];
            for (let i = 0; i < window.localStorage.length; i++) {
                const key = window.localStorage.key(i);
                if (key && (
                    key.includes('selected_') ||           // Selection context
                    key.includes('bite-') ||              // Theme and app-specific
                    key.includes('auth') ||               // Our auth tokens
                    key.includes('user') ||               // User data
                    key.includes('token') ||              // Custom tokens
                    key.includes('session')               // Session data
                )) {
                    keysToRemove.push(key);
                }
            }
            keysToRemove.forEach((key) => window.localStorage.removeItem(key));
        }
        if (window.sessionStorage) {
            window.sessionStorage.clear();
        }
    } catch (storageError) {
        logger.warn('Storage cleanup failed', { error: storageError instanceof Error ? storageError.message : String(storageError) });
    }
};

/**
 * Tears down the session everywhere — resiliently and without ever hanging.
 *
 * Order matters: local teardown (Firebase client SDK + storage) runs FIRST and
 * is effectively instant, so the user is signed out locally immediately. The
 * server call — which clears the httpOnly session cookie and revokes refresh
 * tokens — runs LAST, best-effort and TIME-BOXED. This is the fix for the
 * original bug where the redirect was gated behind an unbounded POST that could
 * hang sign-out for 1–2 minutes. Never throws.
 */
const signOut = async (): Promise<{ message: string }> => {
    logger.debug('Starting resilient signout process');

    // 1. Firebase client SDK (local, instant).
    try {
        await authService.signOut();
        logger.debug('Firebase client SDK cleared');
    } catch (clientSignoutError) {
        logger.warn('Client SDK signout failed (non-fatal)', { error: clientSignoutError instanceof Error ? clientSignoutError.message : String(clientSignoutError) });
    }

    // 2. App-specific storage (local, instant).
    clearLocalAppStorage();

    // 3. Server session teardown — best-effort + time-boxed.
    //    `_retry: true` skips the response interceptor's refresh/retry logic —
    //    a signout request should never trigger session or token refresh.
    try {
        await apiClient.post('/auth/signout', {}, {
            _retry: true,
            timeout: SIGNOUT_SERVER_TIMEOUT_MS,
        } as any);
        logger.info('Server session cleared');
    } catch (serverError) {
        logger.warn('Server signout failed or timed out (non-fatal)', { error: serverError instanceof Error ? serverError.message : String(serverError) });
    }

    return { message: 'Signed out' };
};

/**
 * Validates the current user session by checking server-side session.
 * Returns the current user if session is valid, throws error if not.
 * For admin-app session validation use `validateAdminSession` instead —
 * that endpoint reads `admins/{uid}` directly.
 */
const validateSession = async (): Promise<IUser> => {
    try {
        const response = await apiClient.get('/auth/session') as {
            success: boolean;
            data: {
                user: any;
                sessionToken: string;
                expiresAt: string;
                permissions?: string[];
                tenantId?: string;
                restaurantId?: string;
            };
            meta: any;
        };
        logger.debug('Session validation response received');

        if (!response.success || !response.data?.user) {
            throw new Error('Invalid session response format');
        }

        const userModel = User.fromJSON(response.data.user);
        logger.debug('Session validated for user', { email: userModel.email });
        return userModel.toJSON();
    } catch (error) {
        // A 401/403 here is the expected "no valid API session yet" path on
        // initial load (the app probes the session before its cookie is
        // established) — log it quietly so it doesn't surface as a red console
        // ERROR. Anything else is a genuine problem and stays at warn. The
        // error is rethrown either way, so callers that care still see it.
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 401 || status === 403) {
            logger.debug('Session validation failed (unauthenticated)', { status });
        } else {
            logger.warn('Session validation failed', {
                error: error instanceof Error ? error.message : String(error),
            });
        }
        throw error;
    }
};

/**
 * Signs in as a platform admin. Hits `POST /auth/admin/signin`, which reads
 * `admins/{uid}` (single source of truth) and never touches `users/{uid}`.
 * Returns the full IAdmin record plus session/custom tokens.
 */
const signInAsAdmin = async (
    idToken: string,
    rememberMe: boolean = false,
): Promise<{
    admin: IAdmin;
    sessionToken: string;
    expiresAt: string;
    customToken?: string;
}> => {
    try {
        const response = await apiClient.post('/auth/admin/signin', { idToken, rememberMe }) as any;

        if (!response?.success || !response?.data?.admin) {
            throw new Error('Invalid admin sign-in response format');
        }

        const admin = Admin.fromJSON(response.data.admin).toJSON();
        logger.info('Admin sign-in successful', { uid: admin.uid });

        return {
            admin,
            sessionToken: response.data.sessionToken,
            expiresAt: response.data.expiresAt,
            customToken: response.data.customToken,
        };
    } catch (error) {
        logger.error('Admin sign-in failed', { error: error instanceof Error ? error.message : String(error) });
        throw error;
    }
};

/**
 * Validates the current admin session against `admins/{uid}` on the server.
 * Returns the IAdmin record on success; throws on 401/403 — the calling
 * hook surfaces those as "no admin session" rather than errors.
 */
const validateAdminSession = async (): Promise<IAdmin> => {
    const response = await apiClient.get('/auth/admin/session') as any;

    if (!response?.success || !response?.data?.admin) {
        throw new Error('Invalid admin session response format');
    }

    const admin = Admin.fromJSON(response.data.admin).toJSON();
    logger.debug('Admin session validated', { uid: admin.uid });
    return admin;
};

/**
 * Returns raw session information from the admin API including tenant and restaurant ids
 * This is useful when callers need the session metadata (tenantId, restaurantId, permissions)
 */
const getSessionInfo = async (): Promise<{
    user: any;
    sessionToken?: string;
    expiresAt?: string;
    permissions?: string[];
    tenantId?: string;
    restaurantId?: string;
} | null> => {
    try {
        const response = await apiClient.get('/auth/session') as any;
        if (!response.success || !response.data) return null;
        return response.data;
    } catch (error) {
        logger.warn('Failed to fetch session info', { error: error instanceof Error ? error.message : String(error) });
        return null;
    }
};

/**
 * Requests a Firebase custom token for the currently authenticated server session.
 * Used to reconcile the Firebase client SDK when only a session cookie is present
 * (e.g. after a page refresh with no `?token=` URL parameter).
 */
const getCustomToken = async (options?: { timeoutMs?: number }): Promise<string> => {
    // Optional per-request timeout: the TokenHandler reconcile path passes a
    // short cap (10s) so a single hung /auth/custom-token cannot pin the
    // connection for the global 120s default. No arg = inherit the default,
    // so every existing caller is byte-compatible.
    const config = options?.timeoutMs ? { timeout: options.timeoutMs } : undefined;
    const response = await apiClient.post('/auth/custom-token', {}, config) as any;
    if (!response?.success || !response?.data?.customToken) {
        throw new Error('Invalid custom-token response');
    }
    return response.data.customToken as string;
};

type GrantAssignmentBody =
    | { scope: 'tenant'; tenantDocId: string; role: 'owner' | 'admin' }
    | { scope: 'restaurant'; tenantDocId: string; restaurantDocId: string; role: 'manager' | 'staff' | 'accountant' };

type RevokeAssignmentBody =
    | { scope: 'tenant'; tenantDocId: string; reason?: string; successorUid?: string }
    | { scope: 'restaurant'; tenantDocId: string; restaurantDocId: string; reason?: string; successorUid?: string };

/**
 * Grant a scope-aware assignment to a user. Server auto-links Employee
 * records and syncs Firebase custom claims. Site-admin-only operation
 * (enforced server-side via `requireAdmin` preHandler).
 */
const grantAssignment = async (
    uid: string,
    body: GrantAssignmentBody,
): Promise<{ employeesCreated: number; employeesLinked: number }> => {
    const response = await apiClient.post(`/admin-users/${uid}/assignments`, body) as any;
    return response.data;
};

/**
 * Revoke a scope-aware assignment. Unlinks employee records (records are
 * preserved for history). Site-admin-only.
 */
const revokeAssignment = async (
    uid: string,
    body: RevokeAssignmentBody,
): Promise<void> => {
    await apiClient.delete(`/admin-users/${uid}/assignments`, { data: body } as any);
};

type SuspendAssignmentBody =
    | { scope: 'tenant'; tenantDocId: string; reason?: string }
    | { scope: 'restaurant'; tenantDocId: string; restaurantDocId: string; reason?: string };

type UnsuspendAssignmentBody =
    | { scope: 'tenant'; tenantDocId: string }
    | { scope: 'restaurant'; tenantDocId: string; restaurantDocId: string };

/**
 * Suspend a scope-aware assignment. Temporarily removes access without
 * revoking. Site-admin-only.
 */
const suspendAssignment = async (
    uid: string,
    body: SuspendAssignmentBody,
): Promise<void> => {
    await apiClient.post(`/admin-users/${uid}/assignments/suspend`, body);
};

/**
 * Unsuspend (reinstate) a previously suspended assignment. Site-admin-only.
 */
const unsuspendAssignment = async (
    uid: string,
    body: UnsuspendAssignmentBody,
): Promise<void> => {
    await apiClient.post(`/admin-users/${uid}/assignments/unsuspend`, body);
};

/**
 * Fetch assignment audit history for a user. Returns entries ordered
 * most-recent-first from the `assignment_history` subcollection.
 */
const getAssignmentHistory = async (uid: string): Promise<any[]> => {
    const response = await apiClient.get(`/admin-users/${uid}/assignments/history`) as any;
    return response.data || [];
};

// ── Platform Admin Management (admins collection) ──────────────────────

const listAdmins = async (): Promise<any[]> => {
    const response = await apiClient.get('/admins') as any;
    return response.data || [];
};

const getAdmin = async (uid: string): Promise<any | null> => {
    const response = await apiClient.get(`/admins/${uid}`) as any;
    return response.data || null;
};

const createAdmin = async (data: {
    uid: string;
    email: string;
    displayName?: string;
    firstName?: string;
    lastName?: string;
    siteRole?: string;
    notes?: string;
}): Promise<any> => {
    const response = await apiClient.post('/admins', data) as any;
    return response.data;
};

const updateAdmin = async (uid: string, data: {
    displayName?: string;
    firstName?: string;
    lastName?: string;
    siteRole?: string;
    isActive?: boolean;
    notes?: string;
}): Promise<any> => {
    const response = await apiClient.patch(`/admins/${uid}`, data) as any;
    return response.data;
};

const deactivateAdmin = async (uid: string): Promise<void> => {
    await apiClient.delete(`/admins/${uid}`);
};

/**
 * Idempotent — ensures an `ops.employees` row exists for the given platform
 * admin uid and is bound to their Firebase UID. Required before granting an
 * ops role to a platform admin who carries no implicit `siteRole` floor.
 */
const ensureAdminOpsEmployee = async (uid: string): Promise<any> => {
    const response = await apiClient.post(`/admins/${uid}/ops-employee`) as any;
    return response.data;
};

/**
 * Invite a new platform admin by email. The server provisions the
 * Firebase Auth account if needed, lands the admins/{uid} record, and
 * emails the invitee a password-setup link (or an access-granted notice
 * if they already had an OrderUp account).
 */
const inviteAdmin = async (data: {
    email: string;
    firstName?: string;
    lastName?: string;
    displayName?: string;
    siteRole?: string;
    notes?: string;
}): Promise<{
    admin: IAdmin;
    wasExistingUser: boolean;
    emailSent: boolean;
    emailError: string | null;
}> => {
    const response = await apiClient.post('/admins/invite', data) as any;
    return response.data;
};

/**
 * Resend the platform-admin setup email (regenerates the Firebase
 * password-reset link). Server rejects if the target's email is already
 * verified — at that point they have a working sign-in path and the
 * caller should use the standard password-reset flow instead.
 */
const resendAdminSetupLink = async (
    uid: string,
): Promise<{ emailSent: boolean; emailError: string | null }> => {
    const response = await apiClient.post(`/admins/${uid}/resend-setup-link`) as any;
    return response.data;
};

/**
 * Effective floor permissions for every SiteRole. site_admin returns the
 * compile-time `['*']`; support/viewer return either the persisted edit
 * or the compile-time default (`[]`) when no edit exists yet.
 */
const listSiteRoleFloors = async (): Promise<Record<string, string[]>> => {
    const response = await apiClient.get('/admins/site-role-floors') as any;
    return response.data ?? {};
};

/**
 * Update the editable floor for a non-founder SiteRole. The server
 * rejects `site_admin` (locked at code level) and any non-site_admin
 * caller. On success the permission-service cache is invalidated so the
 * change propagates within one request cycle.
 */
const updateSiteRoleFloor = async (
    siteRole: 'support' | 'viewer',
    permissions: string[],
): Promise<{
    siteRole: 'support' | 'viewer';
    permissions: string[];
    updatedAt: string | Date;
    updatedBy: string;
}> => {
    const response = await apiClient.put(`/admins/site-role-floors/${siteRole}`, {
        permissions,
    }) as any;
    return response.data;
};

export const adminApiService = {
    // User management (admin-users routes)
    listAuthUsers,
    deleteUser,
    getDeletePreview,
    removeUser,
    updateUser,
    createUser,
    signInWithIdToken,
    signInAsAdmin,
    signOut,
    validateSession,
    validateAdminSession,
    getSessionInfo,
    getCustomToken,
    grantAssignment,
    revokeAssignment,
    suspendAssignment,
    unsuspendAssignment,
    getAssignmentHistory,
    // Platform admin management (admins routes)
    listAdmins,
    getAdmin,
    createAdmin,
    updateAdmin,
    deactivateAdmin,
    ensureAdminOpsEmployee,
    inviteAdmin,
    resendAdminSetupLink,
    listSiteRoleFloors,
    updateSiteRoleFloor,
};
